import React from 'react';
import PropTypes from 'prop-types';
import {
  withStyles,
  withMobileDialog,
  Drawer,
  IconButton,
  Tooltip,
  Icon,
  Avatar,
} from '@material-ui/core';
import { FilterList } from '@material-ui/icons';
import { connect } from 'react-redux';
import { toggleDashboardFilter as toggleDashboardFilterAction } from '@pingum/app-state-management/dashboard/actions';

import propTypes from '../../../../utils/propTypes';
import { formatDateString } from '../../../../utils/dateHelper';
import * as brandColors from '../../../../utils/brand-colors';
import Tag from '../../../widgets/Tag';
import styles from './styles';

const noSearchText = 'No search filter';

export const getDashboardSearchText = (dashboard) => {
  const text = (dashboard && dashboard.searchText)
    ? dashboard.searchText
    : '';
  if (text && text.length > 0) {
    return `Text search: ${text}`;
  }
  return noSearchText;
};

export const getStatuses = (ids, statuses) => {
  const data = [];
  for (let i = 0; i < ids.length; i += 1) {
    const statusOption = statuses.filter((status) => status.id === ids[i]);
    if (statusOption && statusOption.length > 0) {
      data.push(statusOption[0]);
    }
  }
  return data;
};

export const getTags = (tagConditions, tagTypes, tags) => {
  const data = [];
  if (tagTypes.length > 0 && tags.length > 0) {
    tagConditions.forEach((tagCondition) => {
      const tagConditionSummary = {
        name: 0,
        tagType: {},
        tags: '',
      };

      tagCondition.forEach((tagId, index) => {
        const tagOption = tags.filter((tag) => tagId === tag.id);
        if (tagOption && tagOption.length > 0) {
          const tagTypeOption = tagTypes.filter((tagType) => tagOption[0].tagType.id === tagType.id);
          if (tagTypeOption && tagTypeOption.length > 0) {
            const [tagType] = tagTypeOption;
            tagConditionSummary.tagType = tagType;
            tagConditionSummary.name += 1;
            tagConditionSummary.tags += `${tagOption[0].name} ${index === tagCondition.length - 1
              ? ''
              : ', '}`;
          }
        }
      });

      tagConditionSummary.name = tagConditionSummary.name.toString();
      data.push(tagConditionSummary);
    });
  }
  return data;
};

export class DashboardFilterHighlightComponent extends React.Component {
  render() {
    const {
      classes,
      filter,
      statuses,
      tags,
      tagTypes,
      fullScreen,
      toggleDashboardFilter,
      selectedView,
    } = this.props;
    const analyticsMode = selectedView.key === 'analytics';

    const hasTextFilter = getDashboardSearchText(filter) !== noSearchText;
    const hasCompleteFilter = (filter.includeComplete && filter.includeComplete === true);
    const hasArchivedFilter = (filter.includeArchived && filter.includeArchived === true);
    const hasDateFilter = filter.startDate;

    return (
      !fullScreen
        && (
          <Drawer
            variant="permanent"
            className={classes.drawer}
            classes={{ paper: classes.drawerOpen }}
            open
            anchor="right"
          >
            <Tooltip title="Filter" placement="left">
              <IconButton
                name="filter-icon-button"
                onClick={toggleDashboardFilter}
                color="inherit"
                className={classes.margin}
              >
                <FilterList />
              </IconButton>
            </Tooltip>

            {
              hasTextFilter
                && (
                  <Tooltip title={getDashboardSearchText(filter)} placement="left">
                    <div className={classes.center}>
                      <IconButton onClick={toggleDashboardFilter}>
                        <Icon
                          style={{ color: brandColors.grey[300] }}
                        >
                          search
                        </Icon>
                      </IconButton>
                    </div>
                  </Tooltip>
                )
            }

            {
              (analyticsMode || hasCompleteFilter)
                && (
                  <Tooltip title="Include completed events" placement="left">
                    <div className={classes.center}>
                      <Icon style={{ color: brandColors.primary.main }}>
                        check_circle
                      </Icon>
                    </div>
                  </Tooltip>
                )
            }

            {
              hasArchivedFilter
                && (
                  <Tooltip title="Include archived events" placement="left">
                    <div className={classes.center}>
                      <Icon style={{ color: brandColors.grey[300] }}>
                        delete
                      </Icon>
                    </div>
                  </Tooltip>
                )
            }

            {
              hasDateFilter
                && (
                  <Tooltip
                    title={`Events started on or after ${formatDateString(filter.startDate)} ${filter.endDate
                      ? `and before ${formatDateString(filter.endDate)}`
                      : ''} `}
                    placement="left"
                  >
                    <div className={classes.center}>
                      <Icon style={{ color: 'rgba(0, 0, 0, 0.45)' }}>
                        date_range
                      </Icon>
                    </div>
                  </Tooltip>
                )
            }

            {
              !hasDateFilter
                && analyticsMode
                && (
                  <Tooltip title="Showing last week of events. While in analytics view a date range is required" placement="left">
                    <div className={classes.center}>
                      <Icon style={{ color: 'rgba(0, 0, 0, 0.45)' }}>
                        date_range
                      </Icon>
                    </div>
                  </Tooltip>
                )
            }

            {
              filter
                && filter.statusIds
                && filter.statusIds.length !== 0
                && getStatuses(filter.statusIds, statuses).map((status) => (
                  <Avatar
                    key={status.id}
                    alt={status.name}
                    style={{
                      color: status.iconColor,
                      width: 30,
                      height: 30,
                      margin: '5px auto',
                    }}
                  >
                    <Icon style={{ fontSize: 18 }}>
                      {status.icon}
                    </Icon>
                  </Avatar>
                ))
            }

            {
              filter
                && filter.tagConditions
                && filter.tagConditions.length !== 0
                && filter.tagConditions[0].length !== 0
                && getTags(filter.tagConditions, tagTypes, tags).map((tagType) => (
                  <Tooltip key={tagType.tagType.id} title={tagType.tags} placement="left">
                    <div style={{ margin: '5px -4px 0px auto' }}>
                      <Tag
                        tag={tagType}
                        icon={tagType.tagType.icon}
                        color={tagType.tagType.iconColor}
                        chipMargin={0}
                      />
                    </div>
                  </Tooltip>
                ))
            }
          </Drawer>
        )
    );
  }
}

DashboardFilterHighlightComponent.propTypes = {
  classes: PropTypes.object,
  filter: PropTypes.object,
  statuses: PropTypes.arrayOf(propTypes.statusShape),
  tags: PropTypes.array,
  tagTypes: PropTypes.array,
  fullScreen: PropTypes.bool,
  toggleDashboardFilter: PropTypes.func,
  selectedView: PropTypes.object,
};

DashboardFilterHighlightComponent.defaultProps = {
  classes: {},
  filter: {
    searchText: '',
    statusIds: [],
    tagConditions: [],
  },
  statuses: [],
  tags: [],
  tagTypes: [],
  fullScreen: false,
  toggleDashboardFilter: () => {},
  selectedView: { key: 'list' },
};

export const mapStateToProps = (state) => ({
  filter: { ...state.dashboard.currentFilterDashboard.dashboardData },
  statuses: state.status.statuses,
  tags: state.tag.tags,
  tagTypes: state.tagType.tagTypes,
  selectedView: state.dashboard.selectedView,
});

export const mapDispatchToProps
  = (dispatch) => ({ toggleDashboardFilter: () => dispatch(toggleDashboardFilterAction(true)) });

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withMobileDialog({ breakpoint: 'xs' })(withStyles(styles, { withTheme: true })(DashboardFilterHighlightComponent)));
